import { useState, useEffect, useContext } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';

import {
  getTeleconsultationById,
  deleteTeleconsultation,
  updateTeleconsultation,
} from 'api/teleconsultations';
import { getAllDoctors } from 'api/doctors';
import { getAllNurses } from 'api/nurses';
import { createStripeCustomer } from 'api/stripe';

import { ReactComponent as ArrowRightSVG } from 'icons/arrow-right.svg';
import { Tabz, TabPane } from 'components/tabz/tabz';
import Details from './view_component/details/admin';
import PaymentIntents from './view_component/payment_intents/admin';

import { nameDecorator } from 'helpers/name';
import { stageToStringMap } from 'config/teleconsultation';
import { UserContext } from 'contexts/user';

import Modal from 'components/modal/delete';
import TCChargeModal from 'components/modal/tc_charge';

import NoAccess from 'pages/no_access';
import Deleted from 'components/deleted/deleted';
import ReminderModal from 'components/reminder/modal';
import TeleconsultationHeaderCard from 'components/cards/teleconsultation_header';

import Loading from 'components/loading/loading';
import { BLANK } from 'config/shared';
import { sanitizeTeleconsultation } from 'helpers/general';
import { serialize } from 'components/text_editor/text_editor_config';
import CustomerHeaderCard from 'components/cards/customer_header';

const TeleconsultationItem = ({ match, onCountUpdate, onMessages }) => {
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [data, setData] = useState(null);
  const [allDoctors, setAllDoctors] = useState([]);
  const [allNurses, setAllNurses] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [nurseOptions, setNurseOptions] = useState([]);

  const { id } = useParams();
  const history = useHistory();
  const { user } = useContext(UserContext);

  const currentTab =
    history.location.search?.replace('?tab=', '').replace('%20', ' ') ||
    'details';

  const handleDelete = async () => {
    setLoading(true);

    await deleteTeleconsultation(id, { id: user.id });

    history.goBack();
    setLoading(false);
  };

  const handleCharge = async (amount) => {
    await createStripeCustomer({
      email: data.customer.email,
      name: `${data.customer?.firstName} ${data.customer?.lastName}`,
      amount,
      teleconsultationId: data.id,
    });

    setTimeout(async () => {
      const { paymentIntents } = await getTeleconsultationById(id);
      setData({ ...data, paymentIntents: [...paymentIntents] });
    }, 2000);

    setChargeModalOpen(false);
  };

  const handleDoctorSelect = (selectedOption) => {
    setData({
      ...data,
      doctor: selectedOption,
    });
  };

  const handleNurseSelect = (selectedOption) => {
    setData({
      ...data,
      nurse: selectedOption,
    });
  };

  const handleSelectChange = (field, selectedOption) => {
    setData({
      ...data,
      [field]: selectedOption.value,
    });
  };

  const handleEditorChange = (field, d) => {
    setData({
      ...data,
      [field]: d,
    });
  };

  const handleSave = async () => {
    setLoading(true);
    const newData = {
      ...data,
      doctorNotes: Array.isArray(data.doctorNotes)
        ? data.doctorNotes.map(serialize).join('')
        : data.doctorNotes,
      adminNotes: Array.isArray(data.adminNotes)
        ? data.adminNotes.map(serialize).join('')
        : data.adminNotes,
    };

    const sanitizedData = sanitizeTeleconsultation(newData);

    const {
      meta: { messages },
    } = await updateTeleconsultation(data.id, sanitizedData);

    setData(newData);

    setLoading(false);
    onCountUpdate();
    onMessages(messages);
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setAllDoctors(await getAllDoctors());
      setAllNurses(await getAllNurses());
      setData(await getTeleconsultationById(match.params.id));
      setLoading(false);
    }
    fetchData();
  }, [match.params.id]);

  // set doctor options
  useEffect(() => {
    const newDoctorOptions = allDoctors
      .map((d) => ({
        id: d.id,
        value: d.id,
        firstName: d.firstName,
        lastName: d.lastName,
        label: `${d.lastName?.toUpperCase()} ${d.firstName}`,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));

    setDoctorOptions([BLANK, ...newDoctorOptions]);
  }, [allDoctors]);

  // set nurse options
  useEffect(() => {
    const newNurseOptions = allNurses.map((n) => ({
      id: n.id,
      value: n.id,
      firstName: n.firstName,
      lastName: n.lastName,
      label: `${n.firstName} ${n.lastName}`,
    }));

    setNurseOptions([BLANK, ...newNurseOptions]);
  }, [allNurses]);

  if (loading) return <Loading />;
  if (data?.statusCode === 403) return <NoAccess />;
  if (data.deletedAt)
    return (
      <Deleted
        deletedBy={{
          email: data.deletedBy.email,
          deletedAt: data.deletedAt,
        }}
      />
    );

  return (
    <>
      <main className="th-main">
        <section className="th-canvas">
          <header className="th-canvas-header post-header">
            <h2 className="th-canvas-title" data-test-screen-title>
              <Link
                className="hover-underline"
                to={`/customers/${data.customer.id}`}
              >
                {nameDecorator(
                  data.customer?.firstName,
                  data.customer?.lastName,
                )}
              </Link>
              <span>
                <ArrowRightSVG />
              </span>
              {stageToStringMap[data.stage]}
            </h2>

            <section className="view-actions">
              <button
                className="btn-white mr5"
                data-cy="reminder"
                onClick={() => setReminderModalOpen(true)}
              >
                <span>Set reminder</span>
              </button>
              {(user.role === 'admin' || user.role === 'doctor') &&
                data.isChargeable && (
                  <button
                    className="btn-white mr5"
                    data-cy="charge"
                    onClick={() => setChargeModalOpen(true)}
                  >
                    <span>Charge</span>
                  </button>
                )}
              {user.role !== 'doctor' && (
                <button
                  className="btn-red"
                  data-cy="delete"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <span>Delete</span>
                </button>
              )}
            </section>
          </header>

          <TeleconsultationHeaderCard data={data} role={user.role} />

          {user.role === 'admin' && (
            <div className="mt10">
              <CustomerHeaderCard
                data={data.customer}
                role={user.role}
                isTeleconsultation
              />
            </div>
          )}

          <Tabz loading={loading} currentTab={currentTab} onSave={handleSave}>
            <TabPane title="Details">
              <Details
                doctorOptions={doctorOptions}
                nurseOptions={nurseOptions}
                teleconsultation={data}
                handleDoctorSelect={handleDoctorSelect}
                handleNurseSelect={handleNurseSelect}
                handleSelectChange={handleSelectChange}
                handleEditorChange={handleEditorChange}
              />
            </TabPane>
            <TabPane title="Payment intents">
              <PaymentIntents paymentIntents={data.paymentIntents} />
            </TabPane>
          </Tabz>
        </section>
      </main>
      {deleteModalOpen && (
        <Modal
          title="Teleconsultation delete"
          content={`Are you sure you want to delete the <b>Teleconsultation</b> of <b>${
            data.customer &&
            data.customer?.firstName + ' ' + data.customer?.lastName
          }</b>?`}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={() => handleDelete()}
        />
      )}
      {chargeModalOpen && (
        <TCChargeModal
          type="tc"
          title="Charge patient for teleconsultation"
          content="You can charge any amount between €0.50 and the tariff. If you decide not to charge the patient, please, input 0."
          tariff={data.tariff}
          onClose={() => setChargeModalOpen(false)}
          onClickOutside={() => setChargeModalOpen(false)}
          onCharge={(amount) => handleCharge(amount)}
        />
      )}
      {reminderModalOpen && (
        <ReminderModal
          onClose={() => setReminderModalOpen(false)}
          onClickOutside={() => setReminderModalOpen(false)}
          customerId={data.customer.id}
        />
      )}
    </>
  );
};

export default TeleconsultationItem;
